import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Loader2, Eye, EyeOff, Check, AlertCircle, Lock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IntegrationCard = ({ provider, displayName, config, onTest, onSave, onAction, actionLabel, saving }) => {
  const title = displayName || provider;
  const [showPasswords, setShowPasswords] = useState({});
  const [localConfig, setLocalConfig] = useState(config);
  const [hasChanges, setHasChanges] = useState(false);
  const [testStatus, setTestStatus] = useState(null);
  const [testing, setTesting] = useState(false);
  const [actionRunning, setActionRunning] = useState(false);

  const handleAction = async () => {
    if (!onAction) return;
    setActionRunning(true);
    try {
      await onAction(provider.toLowerCase());
    } finally {
      setActionRunning(false);
    }
  };

  const handleChange = (key, value) => {
    setLocalConfig({ ...localConfig, [key]: value });
    setHasChanges(true);
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const response = await onTest(provider.toLowerCase());
      setTestStatus(response);
      toast.success(`${title} connection tested`);
    } catch (error) {
      toast.error(`Failed to test ${title}`);
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    await onSave(provider, localConfig);
    setHasChanges(false);
  };

  const getSensitiveKeys = () => ['password', 'key', 'secret', 'token_requester_key', 'api_key'];

  const getProviderFields = () => {
    switch (provider.toLowerCase()) {
      case 'postbank':
        return [
          { key: 'postbank_base_url', label: 'Base URL', type: 'text' },
          { key: 'postbank_username', label: 'Username', type: 'text' },
          { key: 'postbank_password', label: 'Password', type: 'password' },
        ];
      case 'traderoute':
        return [
          { key: 'traderoute_base_url', label: 'Base URL', type: 'text' },
          { key: 'traderoute_merchant_id', label: 'Merchant ID', type: 'text' },
          { key: 'traderoute_password', label: 'Password', type: 'password' },
          { key: 'traderoute_token_requester_id', label: 'Token Requester ID', type: 'text' },
          { key: 'traderoute_token_requester_key', label: 'Token Key', type: 'password' },
          { key: 'traderoute_plu_code', label: 'PLU Code', type: 'text' },
        ];
      case 'virtuallverify':
        return [
          { key: 'virtuallverify_base_url', label: 'Base URL', type: 'text' },
          { key: 'virtuallverify_api_key', label: 'API Key', type: 'password' },
          { key: 'virtuallverify_partner_id', label: 'Partner ID', type: 'text' },
          { key: 'virtuallverify_match_threshold', label: 'Match Threshold (%)', type: 'number' },
        ];
      case 'xml2sms':
        return [
          { key: 'xml2sms_base_url', label: 'Base URL', type: 'text' },
          { key: 'xml2sms_username', label: 'Username', type: 'text' },
          { key: 'xml2sms_password', label: 'Password', type: 'password' },
          { key: 'xml2sms_enabled', label: 'Enabled', type: 'boolean' },
        ];
      case 'limes':
        return [
          { key: 'limes_base_url', label: 'Base URL', type: 'text' },
          { key: 'limes_email',    label: 'Email',    type: 'text' },
          { key: 'limes_secret',   label: 'Secret',   type: 'password' },
          { key: 'limes_tenant',   label: 'Tenant',   type: 'text' },
          { key: 'limes_role',     label: 'Role',     type: 'text' },
          { key: 'limes_enabled',  label: 'Enabled',  type: 'boolean' },
        ];
      default:
        return [];
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle>{title}</CardTitle>
            <CardDescription>Integration credentials and configuration</CardDescription>
          </div>
          {testStatus && (
            <Badge
              variant={testStatus.status === 'ok' ? 'default' : (testStatus.status === 'warning' ? 'secondary' : 'destructive')}
              className={testStatus.status === 'warning' ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100' : ''}
            >
              {testStatus.status === 'ok' ? (
                <><Check size={12} className="mr-1" /> Connected</>
              ) : testStatus.status === 'warning' ? (
                <><AlertCircle size={12} className="mr-1" /> Action needed</>
              ) : (
                <><AlertCircle size={12} className="mr-1" /> Error</>
              )}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {getProviderFields().map((field) => (
          <div key={field.key} className="space-y-2">
            <Label htmlFor={field.key}>{field.label}</Label>
            {field.type === 'boolean' ? (
              <div className="flex items-center gap-2">
                <Switch
                  id={field.key}
                  checked={localConfig[field.key] === 'true' || localConfig[field.key] === true}
                  onCheckedChange={(checked) => handleChange(field.key, checked ? 'true' : 'false')}
                />
                <span className="text-sm text-gray-600">
                  {localConfig[field.key] === 'true' || localConfig[field.key] === true ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            ) : field.type === 'number' ? (
              <Input
                id={field.key}
                type="number"
                step="0.1"
                value={localConfig[field.key] || ''}
                onChange={(e) => handleChange(field.key, e.target.value)}
              />
            ) : field.type === 'password' ? (
              <div className="space-y-2">
                <div className="relative">
                  <Input
                    id={field.key}
                    type={showPasswords[field.key] && localConfig[field.key] !== '••••••' ? 'text' : 'password'}
                    value={localConfig[field.key] || ''}
                    onChange={(e) => handleChange(field.key, e.target.value)}
                    placeholder="••••••"
                    className={localConfig[field.key] === '••••••' ? 'bg-gray-100' : ''}
                    disabled={localConfig[field.key] === '••••••' && showPasswords[field.key]}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (localConfig[field.key] === '••••••') {
                        toast.info('Current password cannot be viewed for security. Enter a new password to change it.');
                      } else {
                        setShowPasswords({ ...showPasswords, [field.key]: !showPasswords[field.key] });
                      }
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 cursor-pointer"
                    title={localConfig[field.key] === '••••••' ? 'Current password cannot be viewed. Enter new password to change.' : 'Show/hide password'}
                  >
                    {localConfig[field.key] === '••••••' ? (
                      <Lock size={16} />
                    ) : showPasswords[field.key] ? (
                      <EyeOff size={16} />
                    ) : (
                      <Eye size={16} />
                    )}
                  </button>
                </div>
                {localConfig[field.key] === '••••••' && (
                  <p className="text-xs text-gray-500 italic">
                    Current password is masked for security. Leave blank to keep existing password, or clear the field and enter a new password to change it.
                  </p>
                )}
              </div>
            ) : (
              <Input
                id={field.key}
                type="text"
                value={localConfig[field.key] || ''}
                onChange={(e) => handleChange(field.key, e.target.value)}
              />
            )}
          </div>
        ))}

        {testStatus && (
          <div className={`p-3 rounded text-sm ${
            testStatus.status === 'ok'
              ? 'bg-green-50 text-green-700'
              : testStatus.status === 'warning'
                ? 'bg-yellow-50 text-yellow-800'
                : 'bg-red-50 text-red-700'
          }`}>
            {testStatus.message}
          </div>
        )}

        <div className="flex gap-2 pt-4 flex-wrap">
          <Button
            onClick={handleTest}
            variant="outline"
            className="flex-1 min-w-[140px]"
            disabled={testing}
          >
            {testing ? (
              <>
                <Loader2 className="animate-spin mr-2" size={16} />
                Testing...
              </>
            ) : (
              'Test Connection'
            )}
          </Button>
          {onAction && actionLabel && (
            <Button
              onClick={handleAction}
              variant="outline"
              className="flex-1 min-w-[140px] border-yellow-400 text-yellow-800 hover:bg-yellow-50"
              disabled={actionRunning}
            >
              {actionRunning ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={16} />
                  Running...
                </>
              ) : (
                actionLabel
              )}
            </Button>
          )}
          <Button
            onClick={handleSave}
            className="flex-1 min-w-[140px] bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34]"
            disabled={!hasChanges || saving}
          >
            {saving ? (
              <>
                <Loader2 className="animate-spin mr-2" size={16} />
                Saving...
              </>
            ) : (
              <>
                <Check className="mr-2" size={16} />
                Save
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export const IntegrationsTab = ({ token }) => {
  const [integrations, setIntegrations] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchIntegrations();
  }, []);

  const fetchIntegrations = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/settings/integrations`, { headers });
      const integrationsData = response.data.integrations || {};
      setIntegrations(integrationsData);
    } catch (error) {
      toast.error('Failed to fetch integration settings');
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async (provider) => {
    try {
      const response = await axios.get(`${API}/admin/settings/integrations/test/${provider}`, { headers });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const handleLimesRegisterAccount = async () => {
    try {
      const response = await axios.post(
        `${API}/limes/setup/store-account-customer`,
        { payload: null },
        { headers }
      );
      if (response.data?.already) {
        toast.info('Mojo Air account customer is already registered');
      } else if (response.data?.ok) {
        toast.success('Mojo Air account customer registered. Re-run Test Connection.');
      } else {
        toast.error(`Mojo Air registration failed: HTTP ${response.data?.status}`);
      }
    } catch (error) {
      const detail = error?.response?.data?.detail || error?.message || 'Unknown error';
      toast.error(`Mojo Air registration failed: ${detail}`);
    }
  };

  const handleSaveIntegration = async (provider, config) => {
    setSaving(true);
    try {
      const settingsData = {};
      Object.entries(config).forEach(([key, value]) => {
        settingsData[key] = value;
      });

      const response = await axios.put(`${API}/admin/settings/integrations`, {
        settings: settingsData
      }, { headers });

      toast.success(`${provider} settings saved`);
      fetchIntegrations();
    } catch (error) {
      toast.error(`Failed to save ${provider} settings`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="animate-spin" size={24} />
      </div>
    );
  }

  const getProviderConfig = (provider) => {
    const config = {};
    const providerPrefix = provider.toLowerCase().replace('xml2sms', 'xml2sms_').replace('virtuallverify', 'virtuallverify_').replace('traderoute', 'traderoute_').replace('postbank', 'postbank_');

    Object.entries(integrations).forEach(([key, val]) => {
      if (key.startsWith(providerPrefix)) {
        config[key] = val.value;
      }
    });

    return config;
  };

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> Passwords and sensitive keys are masked with dots (••••••). Leave them as-is to keep current values.
        </p>
      </div>

      <IntegrationCard
        provider="Postbank"
        config={getProviderConfig('postbank')}
        onTest={handleTestConnection}
        onSave={handleSaveIntegration}
        saving={saving}
      />

      <IntegrationCard
        provider="Traderoute"
        config={getProviderConfig('traderoute')}
        onTest={handleTestConnection}
        onSave={handleSaveIntegration}
        saving={saving}
      />

      <IntegrationCard
        provider="VirtuallVerify"
        config={getProviderConfig('virtuallverify')}
        onTest={handleTestConnection}
        onSave={handleSaveIntegration}
        saving={saving}
      />

      <IntegrationCard
        provider="XML2SMS"
        config={getProviderConfig('xml2sms')}
        onTest={handleTestConnection}
        onSave={handleSaveIntegration}
        saving={saving}
      />

      <IntegrationCard
        provider="Limes"
        displayName="Mojo Air (Limes)"
        config={getProviderConfig('limes')}
        onTest={handleTestConnection}
        onSave={handleSaveIntegration}
        onAction={handleLimesRegisterAccount}
        actionLabel="Register Account Customer"
        saving={saving}
      />
    </div>
  );
};
